import React from 'react'
import { Link } from '@reach/router'
import styled from 'styled-components'

// utils
import breakpoints from 'utils/breakpoints'
import th from 'utils/themeHelper'
import spacing from 'utils/spacing'

// config
import l from 'config/localization'

// assets
import orgIcon from 'assets/images/icon-group.svg'
import { ReactComponent as OppIcon } from 'assets/images/svgs/icon-org-page-opportunity.svg'

import Tooltip from 'components/Tooltip'

const Text = styled.div`
  color: ${props => props.color || '#ffffff'};
  font-family: ${th('fonts.bold')};
  font-size: ${props => `${props.size}px` || 'initial'};
  line-height: 1.2em;
  margin: 0 0 0.25em;
  padding: 0;
`

export const FaqContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  margin-top: 65px;
  width: 100%;

  @media screen and (min-width: 769px) {
    margin-bottom: ${spacing.gigantic}px;
    min-height: calc(100vh - 451px);
  }
`

export const FaqSection = styled.div`
  width: 100%;

  @media screen and (min-width: 769px) {
    margin-bottom: 50px;
  }
`

export const VisibleFaqLabel = styled.div`
  ${Text};

  font-family: ${th('fonts.bold')};
  font-size: 20px;
  margin: 0 0 10px;
  text-align: left;
`

export const FaqQuestion = styled.h3`
  font-size: 25px;
  margin: 0;
`

export const FaqShortcutsWrapper = styled.div`
  line-height: 1.25;

  @media screen and (min-width: 769px) {
    max-width: 400px;
  }
`

export const FaqCategoryGroupLabel = styled.h3`
  font-size: 16px;
  margin: 20px 0 10px;
`

export const OrgStoryFeedWrapper = styled.div`
  flex: 1;
`

export const FaqShortcutLabel = styled.div`
  cursor: pointer;
  font-size: 14px;
  margin-top: 10px;
  text-decoration: underline;
`

export const FaqAnswer = styled.div`
  font-size: 18px;
  line-height: 1.25;
  margin-top: 15px;
`

export const FaqCategory = styled.div`
  background: ${th('secondary.light')};
  border: 1px solid black;
  border-radius: 4px;
  font-size: 10px;
  line-height: 1.25;
  margin: 0 auto 12px;
  max-width: max-content;
  padding: 5px;

  @media screen and (min-width: 769px) {
    margin: 0 0 12px;
  }
`

export const CampaignName = styled.div`
  ${Text};
  font-family: ${th('fonts.bold')};
  font-size: 20px;
  margin-top: 16px;
  width: 100%;
`

export const OrgContainer = styled.div`
  margin: 65px auto 0;
  width: 100%;
`

export const OrgIntroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 10px 30px;

  @media screen and (min-width: 769px) {
    flex-direction: row;
    margin: 30px auto 30px;
    max-width: 1300px;
    width: 80%;
  }
`

export const OrgSectionWrapper = styled.div`
  margin: 20px;

  @media screen and (min-width: 769px) {
    display: flex;
    margin: 50px 200px;
  }
`

export const UserSectionWrapper = styled.div`
  margin: 20px 10px;

  @media screen and (min-width: 769px) {
    display: flex;
    margin: 30px auto;
    max-width: 1300px;
    width: 80%;
  }
`

export const OrgIntroInnerWrapper = styled.div`
  flex: 1;
  margin-bottom: 20px;

  @media screen and (min-width: 769px) {
    flex: 0;
    margin-bottom: 0;
    margin-right: ${props => (props.marginRight ? '40px' : '0')};
    min-width: 400px;
    max-width: 400px;
  }
`

export const OrgActivityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 70px 10px 50px;

  @media screen and (min-width: 769px) {
    flex-direction: row;
    margin: 100px 20px 0 200px;
  }
`

export const UserSectionWrapperInner = styled.div`
  line-height: 1.5;

  @media screen and (min-width: 769px) {
    max-width: 100%;
    width: 100%;
  }
`

export const OrgDescriptionLabel = styled.div`
  ${Text};

  font-family: ${th('fonts.bold')};
  font-size: 20px;
  margin: 0 0 10px;
  text-align: center;

  @media screen and (min-width: 769px) {
    text-align: left;
  }
`

export const OrgProfileHero = styled.div`
  background-image: url(${props => props.background});
  background-size: cover;
  background-position: 50% 50%;
  background-repeat: no-repeat;
  height: 180px;
  position: relative;
  width: 100%;

  @media screen and (min-width: 769px) {
    height: 150px;
  }
`

export const ImageBackground = styled.div`
  align-items: flex-end;
  background-color: ${th('backgrounds.mid')};
  background-image: url(${props => props.background});
  background-position: center;
  background-size: cover;
  border-radius: 20px;
  display: flex;
  height: 225px;
  width: 100%;
`

export const SectionCell = styled.div`
  flex: 1 1 calc(50% - 8px);
  margin: 0 8px;
  max-width: 225px;
  min-width: 225px;
  padding: 8px 0;
  text-align: ${props => (props.center ? 'center' : props.rightAlign ? 'right' : 'left')};

  @media screen and (min-width: 769px) {
    margin: 0 15px 15px 0;
  }

  a {
    color: ${th('text.dark')};
  }

  &:hover a {
    text-decoration: underline;
  }

  &:hover ${ImageBackground} {
    background-image: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.05) 100%),
      url(${props => props.background});
    overflow: hidden;
    transition: all 0.5s;
  }
`

export const CampaignsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  width: 100%;

  @media screen and (min-width: 780px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 30px 0;
  }
`

export const SocialMediaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: ${breakpoints.largeMobile}) {
    margin: 0;
    justify-content: center;
  }
`

export const OrgSharePageDescription = styled.div`
  font-size: 16px;
  line-height: 1.25em;
  margin: 0 0 15px;
  text-align: left;
`

export const IconContainer = styled.div`
  color: ${th('primary.base')};
  margin-right: 15px;
  width: 35px;
`

export const IconLink = styled(Link)`
  color: ${th('text.dark')};
  display: ${props => props.display || 'flex'};
  justify-content: center;
  margin: 10px 0 0;
  text-decoration: none;
`

export const IconLinkDisabled = styled.div`
  color: ${th('text.dark')};
  display: block;
  justify-content: center;
  margin: 10px 0 0;
  text-decoration: none;
`

export const OrgAdminActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 10px auto;
  max-width: max-content;

  @media screen and (min-width: 769px) {
    justify-content: flex-start;
    margin: 10px 0;
  }
`

export const EditIconWrapper = styled.div`
  align-items: center;
  background: white;
  border: 1px solid ${th('borders.default')};
  border-radius: 4px;
  color: ${props => (props.disabled ? th('text.light') : th('text.dark'))};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  height: 40px;
  justify-content: center;
  line-height: 1.75;
  padding: 12px;
  white-space: nowrap;

  > svg {
    height: ${props => `${props.size || 30}px`};
    margin-right: 5px;
    padding: ${props => `${props.iconPadding || 0}`};
    width: ${props => `${props.size || 30}px`};
  }

  &:hover {
    border: 1px solid ${props => (props.disabled ? th('borders.default') : th('borders.medium'))};
  }

  @media screen and (min-width: 769px) {
    justify-content: unset;
  }
`

export const VerificationWrapper = styled.div`
  display: block;
  margin: 0 0 20px;

  @media screen and (min-width: 769px) {
    margin: -20px 0 20px;
  }
`

export const VerificationStatus = styled.div`
  color: ${th('text.dark')};
  display: inline-block;
  font-family: ${th('fonts.bold')};
  font-size: 15px;
  margin-left: 8px;
  vertical-align: middle;
`

export const VerificationIcon = styled.img`
  display: inline-block;
  width: 35px;
  height: 35px;
  vertical-align: middle;
`

export const UserIcon = ({
  active,
  hoverException,
  icon: Icon,
  iconPadding,
  padding,
  small,
  svgHeight,
  svgMarginLeft,
  svgMarginTop,
  svgWidth
}) => {
  return (
    <IconWrapper
      active={active}
      hoverException={hoverException}
      iconPadding={iconPadding}
      padding={padding}
      small={small}
      svgHeight={svgHeight}
      svgMarginLeft={svgMarginLeft}
      svgMarginTop={svgMarginTop}
      svgWidth={svgWidth}
    >
      <Icon />
    </IconWrapper>
  )
}

export const OrgNavItemLabel = styled.div`
  display: none;

  @media screen and (min-width: 1300px) {
    display: block;
    font-size: 13px;
  }
`

export const TabStat = styled.div`
  align-items: center;
  background: ${props => (props.active ? th('secondary.base') : 'white')};
  color: #484848;
  display: ${props => (props.mobileOnly ? 'inline-block' : 'none')};
  font-family: ${th('fonts.bold')};
  font-size: 20px;
  padding: ${props => (props.noPadding ? '0 3px' : '0 5px 15px 5px')};
  position: relative;
  text-decoration: none !important;

  @media screen and (min-width: 769px) {
    border: 1px solid #484848;
    border-radius: 2px;
    display: flex;
    font-size: 10px;
    left: 6px;
    padding: 2px 3px 1px;
    top: -1px;
  }
`

export const IconWrapper = styled.div`
  background: ${props => (props.hoverException ? 'transparent' : 'white')};
  border-radius: 50%;
  cursor: pointer;
  height: ${props => (props.small ? '20px' : '40px')};
  margin: 0 5px;
  overflow: hidden;
  padding: 0;
  width: ${props => (props.small ? '20px' : '40px')};

  & > svg {
    background: transparent;
    height: ${props => props.svgHeight || '40px'};
    margin-left: ${props => props.svgMarginLeft || '0px'};
    margin-top: ${props => props.svgMarginTop || '0px'};
    padding: ${props => props.iconPadding || '0px'};
    width: ${props => props.svgWidth || '40px'};
  }

  &:hover {
    background: ${props => (props.hoverException ? 'transparent' : 'white')};
  }
`

export const ProfileLink = ({
  disabled,
  disabledHint,
  display,
  icon: Icon,
  iconPadding,
  label,
  linkUrl = '',
  size
}) => {
  if (disabled) {
    return (
      <Tooltip content={disabledHint} forceShow minWidth="250px" position="right">
        <IconLinkDisabled>
          <EditIconWrapper disabled iconPadding={iconPadding} size={size}>
            <Icon />
            {label}
          </EditIconWrapper>
        </IconLinkDisabled>
      </Tooltip>
    )
  }

  return (
    <IconLink display={display} to={linkUrl}>
      <EditIconWrapper iconPadding={iconPadding} size={size}>
        <Icon />
        {label}
      </EditIconWrapper>
    </IconLink>
  )
}

export const ViewMoreLink = styled(Link)`
  border: 1px solid ${th('borders.default')};
  border-radius: 4px;
  color: ${th('text.dark')};
  cursor: pointer;
  display: block;
  font-size: 12px;
  margin: 0 auto;
  max-width: max-content;
  padding: 5px 10px;
  position: relative;

  &:hover {
    border: 1px solid ${th('borders.dark')};
  }
`

export const SidebarIcon = styled.img`
  color: #a8db43;
  width: 35px;
  margin-right: 15px;
`

export const SideContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  margin-right: 8px;

  @media screen and (min-width: 769px) {
    flex: 3;
    margin-left: 0;
    margin-right: 20px;
  }
`

export const MainContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  margin-left: 8px;
  margin-right: 8px;

  @media screen and (min-width: 769px) {
    margin-left: 0;
    margin-right: 0;
    max-height: 1000px;
    overflow-y: scroll;
    min-width: 570px;
  }
`

export const OrgStatusWarning = styled.div`
  background: ${th('text.error')};
  border-radius: 3px;
  color: white;
  font-family: ${th('fonts.bold')};
  line-height: 1.35em;
  padding: 10px 15px;
  margin: 1.25em 0;

  @media screen and (min-width: 769px) {
    border-radius: 0;
    font-size: 13px;
    margin: 0 0 20px;
    padding: 5px 10px;
    max-width: max-content;
    white-space: normal;
    word-wrap: break-word;
  }
`

export const OrgMembershipCardTitle = styled.div`
  ${Text};
  font-family: ${th('fonts.bold')};
  font-size: 30px;
  margin: 0 0 15px;
  text-align: center;
  display: ${props => (props.wideOnly ? 'none' : 'block')};

  @media screen and (min-width: 769px) {
    display: ${props => (props.mobileOnly ? 'none' : 'block')};
    margin: 0 0 20px;
  }
`

export const OrgName = styled.div`
  ${Text};
  font-family: ${th('fonts.bold')};
  font-size: 30px;
  margin: 10px 0 5px;
  text-align: center;

  @media screen and (min-width: 769px) {
    margin: 0 0 5px;
    text-align: left;
  }
`

export const OrgNavWrapperItem = styled.div`
  align-items: center;
  background: ${props => (props.active ? th('primary.base') : 'white')};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  font-family: ${th('fonts.bold')};
  margin: 0 5px;
  padding: 4px 8px;
  position: relative;

  @media screen and (min-width: 769px) {
    display: ${props => (props.mobileOnly ? 'none' : 'flex')};
    margin: 0 5px;
  }

  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;

  ${OrgNavItemLabel} {
    color: ${props => (props.active ? 'white' : th('text.dark'))};
  }

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  &:hover {
    background: ${th('primary.base')};
    cursor: pointer;
  }

  &:hover ${OrgNavItemLabel} {
    color: white;
  }
`

export const OrgSocialMediaHeader = styled.div`
  ${Text};
  font-family: ${th('fonts.bold')};
  font-size: 20px;
  text-align: center;
  width: 100%;

  @media screen and (min-width: 769px) {
    text-align: left;
  }
`

export const OrgSocialMediaLogo = styled.img`
  height: 35px;
  width: 35px;
`

export const ActivityCardWrapper = styled.div`
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.08);
  flex: 1;
  flex-grow: 0;
  flex-basis: 100%;
  margin-bottom: 20px;

  @media screen and (min-width: 600px) {
    flex-basis: calc(50% - 20px);
    margin-bottom: 40px;
    max-width: 585px;
  }
`

export const BenefactionAvatarContainer = styled.div`
  position: relative;
  width: 100%;
`

export const BenefactionAvatarWrapper = styled.div`
  background: white;
  border: 1px solid ${th('borders.default')};
  border-radius: 50%;
  height: 160px;
  margin: 20px auto;
  overflow: hidden;
  position: relative;
  width: 160px;
`

export const BenefactionsWrapper = styled.div`
  display: flex;
  flex: 1;
`

export const BenefactionsOuterWrapper = styled.div`
  flex: 1;
  width: 100%;
`

export const BenefactionCardWrapper = styled.div`
  cursor: pointer;
  flex: 1;
  flex-grow: 0;
  flex-basis: 100%;
  margin-bottom: 20px;

  @media screen and (min-width: 600px) {
    flex-basis: calc(50% - 20px);
    margin-bottom: 40px;
    margin-right: 20px;
    max-width: 585px;
  }
`

export const BenefactionCardInnerWrapper = styled.div`
  background: white;
  border-color: ${th('borders.medium')};
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  min-height: 250px;
  overflow: hidden;
  position: relative;

  &:hover {
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
  }
`

export const BenefactionHeader = styled.div`
  margin: 10px auto;
  font-size: 15px;
  font-family: ${th('fonts.bold')};
`

export const OrgDetails = styled.div`
  background: white;
  display: ${props => (props.mobileOnly ? 'block' : 'none')};
  margin-left: -10px;
  margin-right: -10px;
  padding: 0 15px;
  position: relative;
  text-align: center;

  @media screen and (min-width: 769px) {
    display: ${props => (props.mobileOnly ? 'none' : 'block')};
    text-align: left;
    max-width: 300px;
  }

  @media screen and (min-width: 969px) {
    max-width: 400px;
  }
`

export const OrgDetailsWrapper = styled.div`
  display: block;
`

export const OrgDetailFallback = styled.span`
  color: rgba(72, 72, 72, 0.6);
  font-family: ${th('fonts.italic')};
  line-height: 1.2;
`

export const OrgDetailLabel = ({ fallback, label }) => {
  if (label) return label

  return <OrgDetailFallback>{fallback}</OrgDetailFallback>
}

export const OrgType = styled.h5`
  line-height: 1.25em;
  margin: 0 auto 20px;
  text-transform: initial;

  @media screen and (min-width: 730px) {
    width: 80%;
  }
`

export const PreferredCategory = styled.div`
  background: ${th('backgrounds.light')};
  border: 1px solid ${th('borders.dark')};
  border-radius: 4px;
  font-size: 12px;
  margin-right: 4px;
  padding: 1px 5px;
  white-space: nowrap;
`

export const OrgLogoContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 4;

  @media screen and (min-width: 769px) {
    bottom: 10px;
    width: calc(100% - 200px);
  }
`

export const OrgLogoWrapper = styled.div`
  background: white;
  border: 1px solid ${th('borders.default')};
  border-radius: 50%;
  height: 160px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  width: 160px;

  @media screen and (min-width: 769px) {
    display: inline-block;
    margin: 0;
  }
`

export const OrgLogo = styled.img`
  aspect-ratio: 1;
  background: ${props => (props.hasAvatar ? th('backgrounds.dark') : 'white')};
  position: absolute;
  width: 160px;
  object-fit: cover;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export const OrgContactDetails = styled.div`
  margin-top: 22.5px;
`

export const OrgMembershipButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px auto;
  text-align: center;
  width: 100%;

  @media screen and (min-width: 769px) {
    justify-content: flex-start;
    margin: 10px 0;
  }
`

export const ClaimOrgDetails = styled.div`
  font-size: 18px;
  line-height: 1.25;
  margin: 0 auto 22.5px;
  text-align: center;
  width: 90%;

  @media screen and (min-width: 769px) {
    font-size: 15px;
    margin: 0 0 20px;
    text-align: left;
    width: 70%;
  }
`

export const OrgPageMessageContainer = styled.div`
  margin: 65px 0 0;
`

export const OrgDescription = styled.div`
  font-size: 16px;
  line-height: 1.35em;
`

export const FullDescriptionButtonWrapper = styled.div`
  margin: 10px 0;

  svg {
    height: 10px;
    margin-top: -2px;
    margin-right: 8px;
    width: 10px;
  }
`

export const OrgDescriptionFull = styled.div`
  font-size: 16px;
  line-height: 1.25em;
  text-align: left;
`

export const OrgProfileHorizontalSeparator = styled.div`
  background: ${th('borders.medium')};
  height: 2px;
  margin: 0 10px;
  width: calc(100% - 20px);

  @media screen and (min-width: 769px) {
    margin: 0 auto;
    width: 80%;
  }
`

export const OrgContactDetailRow = styled.div`
  align-items: center;
  display: flex;
  font-size: 16px;
  justify-content: flex-start;
  line-height: 1.25em;
  margin-bottom: 22.5px;
  margin-top: 5px;
  text-align: left;

  @media screen and (min-width: 769px) {
    margin: 0 auto 15px;
  }
`

export const OrgNavItem = styled.div`
  border-bottom: 2px solid ${th('secondary.light')};
  border-bottom-width: ${props => (props.active ? '4px' : 0)};
  font-weight: ${props => (props.active ? 'bold' : 'normal')};
  padding: 1.25em 2em;
  cursor: pointer;

  @media screen and (min-width: 600px) {
    padding: 1.25em;
  }
`

export const FloatingOrgNavWrapper = styled.div`
  bottom: 10px;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  position: fixed;
  width: 100%;
  z-index: 10;

  @media screen and (min-width: 769px) {
    display: block;
    width: 100%;
  }
`

export const FloatingOrgNavWrapperInner = styled.div`
  align-items: center;
  background: ${th('backgrounds.dark')};
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 10px;
  width: 96%;

  @media screen and (min-width: 769px) {
    font-family: ${th('fonts.bold')};
    font-size: 15px;
    justify-content: space-between;
    margin: 0 auto;
    position: relative;
    width: max-content;
  }
`

export const CurrentTabLabel = styled.div`
  border: none;
  box-shadow: none;
  display: block;
  font-family: ${th('fonts.bold')};
  font-size: 20px;
  margin: 0;
  padding: 10px 0;

  @media screen and (min-width: 769px) {
    display: none;
  }
`

export const MainContent = styled.div`
  @media screen and (min-width: 769px) {
    width: 100%;
  }
`

export const OrgMembershipCardImage = styled.img`
  margin: 0 auto ${props => props.bottom || '0'};
  position: relative;
  top: ${props => props.top || '0'};
  width: 90%;

  @media screen and (min-width: 769px) {
    width: 60%;
  }
`

export const OrgGoalImage = styled.div`
  background-color: ${th('backgrounds.dark')};
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center center;
  border-bottom: 1px solid ${th('borders.default')};
  border-radius: 4px 4px 0 0;
  height: 150px;
  width: 100%;
`

export const OrgGoalDeadlineWrapper = styled.div`
  border: 1px solid ${th('borders.darkPill')};
  border-radius: 4px;
  display: flex;
  font-size: 12px;
  height: 24px;
  left: 10px;
  position: absolute;
  max-width: max-content;
  top: 10px;
`

export const OrgGoalDeadlineWrapperLabel = styled.span`
  align-items: center;
  background: ${th('borders.darkPill')};
  color: white;
  display: flex;
  height: 100%;
  padding: 0 5px;
`

export const OrgGoalPillsWrapper = styled.div`
  display: flex;
  margin-top: 10px;
`

export const OrgGoalCategory = styled.span`
  align-items: center;
  background: white;
  border: 1px solid ${th('borders.required')};
  border-radius: 4px;
  display: flex;
  max-width: max-content;
  padding: 5px;
  right: 10px;
  position: absolute;
  top: 10px;
`

export const OrgGoalDeadlineWrapperValue = styled.span`
  align-items: center;
  background: white;
  border-radius: 0 4px 4px 0;
  display: flex;
  height: 100%;
  padding: 0 8px;
`

export const OrgGoalsWrapper = styled.div`
  margin: 0 auto;

  @media screen and (min-width: 769px) {
    margin: 0;
    max-width: 600px;
  }
`

export const OrgGoalDescription = styled.div`
  margin: 0 10px 10px 0;
  line-height: 1.2;
`

export const MilestonesLabel = styled.h4`
  border-top: 1px solid ${th('borders.default')};
  font-size: 15px;
  margin: 0 0 10px;
  padding-top: 10px;
`

export const SingleGoalMilestone = styled.div`
  display: block;
  margin-top: 10px;
`

export const SingleGoalMilestoneDate = styled.div`
  font-size: 12px;
  max-width: max-content;
`

export const SingleGoalMilestoneDetails = styled.div`
  font-family: ${props => (props.hasNotes ? th('fonts.regular') : th('fonts.italic'))};
  line-height: 1.2;
  margin-top: 10px;
`

export const SingleGoalMilestoneTitle = styled.div`
  cursor: pointer;
  font-family: ${th('fonts.bold')};
  margin-bottom: 3px;
  max-width: max-content;
  text-decoration: underline;
`

export const OrgGoalTitle = styled.h4`
  margin: 0 0 10px;
`

export const OrgGoalWrapper = styled.div`
  border: 1px solid ${th('borders.default')};
  border-radius: 4px;
  margin: 0 0 20px;
  position: relative;
  width: 100%;
`

export const OrgGoalDetailsWrapper = styled.div`
  padding: 10px;
`

export const OrgMembershipCardWrapper = styled.div`
  border: 2px solid ${th('borders.required')};
  border-radius: 4px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  width: 90%;

  @media screen and (min-width: 769px) {
    margin: 0;
    width: 100%;
  }
`

export const MainContentLeftPadding = styled.div`
  @media screen and (min-width: 769px) {
    max-width: 700px;
    padding-left: 50px;
    width: 100%;
  }
`

export const NotFoundWrapper = styled.div`
  margin: 65px 0;
  width: 96%;

  @media screen and (min-width: 600px) {
    margin: 80px auto;
    width: 60%;
  }
`

export const MissingOrgText = styled.div`
  line-height: 1.25em;
  margin: 3em auto;
  text-align: center;
`

export const ButtonsDivider = styled.div`
  margin-bottom: 15px;
`

export const MissingOrgTitle = styled.h1`
  text-align: center;
`

export const ActionLink = styled(Link)`
  display: inline-block;
  text-decoration: none;
`

export const ExternalLink = styled.a`
  color: ${th('text.dark')};
  display: inline-block;
  font-size: 16px;
  text-decoration: underline;
`

const UserSupportedOrg = ({ org }) => {
  return (
    <SupportedOrgWrapper to={`/${l('ROUTE_ORGS')}/${org.url_name}` || '#'}>
      <SupportedOrgLogo src={org.logo_image || orgIcon} />

      <SupportedOrgNameWrapper>
        <SupportedOrgName>{org.name}</SupportedOrgName>
      </SupportedOrgNameWrapper>
    </SupportedOrgWrapper>
  )
}

const UserProfileOpp = ({ opp }) => {
  return (
    <SupportedOrgWrapper to={`/${l('ROUTE_OPPS')}/${opp.id}` || '#'}>
      <SupportedOrgLogo src={opp.image_url || OppIcon} />

      <SupportedOrgNameWrapper>
        <SupportedOrgName>{opp.title}</SupportedOrgName>
      </SupportedOrgNameWrapper>
    </SupportedOrgWrapper>
  )
}

const SupportedOrgNameWrapper = styled.div`
  margin: 10px;
`

const SupportedOrgName = styled.h4`
  color: ${th('text.dark')};
  font-size: 14px;
  margin: 0;
  vertical-align: middle;
`

export const SupportedOrgWrapper = styled(Link)`
  background: white;
  border: 1px solid ${th('borders.medium')};
  border-radius: 5px;
  box-shadow: ${props => (props.dark ? `none` : `0 0 10px 0 rgba(0, 0, 0, 0.15)`)};
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  overflow: hidden;
  position: relative;
  text-decoration: none;

  &:hover {
    box-shadow: 0 0 4px #379b75;
    cursor: pointer;
  }

  @media screen and (min-width: 769px) {
    flex-basis: calc(25% - 10px);
    margin-right: 20px;

    &:last-of-type {
      margin-right: 0;
    }
  }
`

const UserSupportedOrgsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 769px) {
    flex-direction: row;
    justify-content: flex-start;
  }
`

const SupportedOrgLogo = styled.div`
  background-image: url(${({ src }) => src});
  background-size: cover;
  background-position: center center;
  border-radius: 4px 4px 0 0;
  width: 100%;
  height: 150px;
`

export const UserFullActivityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 20px 30px;

  @media screen and (min-width: 769px) {
    flex-direction: row;
    margin: 30px 50px 30px 10%;
    width: calc(90% - 50px);
  }
`

export const UserFullActivityWrapperInner = styled.div`
  flex: 1;
  margin-bottom: 20px;

  @media screen and (min-width: 769px) {
    margin-bottom: 0;
  }
`

export const UserFullActivityContainer = styled.div`
  line-height: 1.5;

  @media screen and (min-width: 769px) {
    flex: 2;
  }
`

export const UserCountryIconWrapper = styled.div`
  > svg {
    height: 20px;
    margin-right: 10px;
    width: 20px;
  }
`

export const UserCountryWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 25px;

  @media screen and (min-width: 769px) {
    justify-content: flex-start;
    margin-bottom: 20px;
  }
`

export const UserDetailFallback = styled.span`
  color: rgba(72, 72, 72, 0.6);
  font-family: ${th('fonts.italic')};
  line-height: 1.2;
`

export const UserProfileStatsImage = styled.img`
  height: 43px;
  width: 30px;

  @media screen and (min-width: 769px) {
    height: 50px;
    width: 35px;
  }
`

export const UserProfileStatsImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-right: 8px;
  width: 30px;

  @media screen and (min-width: 769px) {
    margin-right: 20px;
    width: 80px;
  }
`

export const UserProfileStatsIcon = styled.div`
  align-content: center;
  display: flex;
  margin-right: 8px;
  width: 30px;

  @media screen and (min-width: 769px) {
    margin-right: 20px;
    width: 80px;
  }

  & > svg {
    width: 30px;
    height: 30px;

    @media screen and (min-width: 769px) {
      width: 80px;
      height: 80px;
    }
  }
`

export const UserProfileStatLabel = styled.div`
  line-height: 1.2;
  font-size: 12px;

  @media screen and (min-width: 769px) {
    font-size: 15px;
    white-space: nowrap;
  }
`

export const UserProfileStatValue = styled.div`
  font-family: ${th('fonts.bold')};
  font-size: 16px;
  margin-right: 8px;

  @media screen and (min-width: 769px) {
    font-size: 20px;
    margin-right: 10px;
  }
`

export const UserProfileStatsRow = styled.div`
  align-items: center;
  background: ${th('transparent.base')};
  border: 2px solid ${th('borders.required')};
  border-radius: 14px;
  display: flex;
  flex-basis: calc(50% - 10px);
  margin-bottom: 15px;
  padding: 10px;

  @media screen and (min-width: 769px) {
    padding: 20px;
  }
`

export const UserProfileStatsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  justify-content: space-between;
`

export const UserSupportedOrgsBlock = ({ individual, supportedOrgs }) => {
  if (!supportedOrgs.length) {
    return (
      <UserDetailFallback>
        {individual.f_name} has not supported any nonprofits yet.
      </UserDetailFallback>
    )
  }

  return (
    <UserSupportedOrgsWrapper>
      {supportedOrgs.map(org => {
        return <UserSupportedOrg org={org} key={org.id} />
      })}
    </UserSupportedOrgsWrapper>
  )
}

export const UserOppsBlock = ({ opps }) => {
  return (
    <UserSupportedOrgsWrapper>
      {opps.map(opp => {
        return <UserProfileOpp opp={opp} key={opp.id} />
      })}
    </UserSupportedOrgsWrapper>
  )
}
